import React from 'react';

import { useAppContext } from 'src/components/app-context';
import { useProductContext } from '../product-context';
import { useOfferListFilterContext } from '../offer-list-filter-context';

import { filterOffers } from 'src/components/price-list/filtering';
import { ProductConditions } from '../product-conditions';
import { SizeVariants } from '../product-variants/size-variants';
import { ColorVariants } from '../product-variants/color-variants';
import { FilterType } from 'src/components/price-list/types';
import { SizeVariantsButtonLabel } from '../product-variants/size-variants/size-variants-button-label';
import { ColorVariantsButtonLabel } from '../product-variants/color-variants/color-variants-button-label';
import { ModalView } from './modal-view';

import { getVariantValues } from '../product-variants/utils/common';
import { getProductConditionValues } from '../product-conditions/utils';
import { isWhitelistedForColors } from '../product-variants/color-variants/utils/whitelist';
import { isWhitelistedForSizes } from '../product-variants/size-variants/utils/whitelist';
import { isBlacklistedForColors } from '../product-variants/color-variants/utils/blacklist';

import { useShowAllVariantsExperiment } from 'src/hooks/experiments';

import type { PriceList } from 'src/types';
import type { IVariantValue, VariantType } from '../product-variants/types';
import type { IFilterComponent, IProductFilterComponents } from './types';

import { Filter } from './styles';

export const ProductFilterComponents: React.FC<
  IProductFilterComponents
> = props => {
  const { isMobile } = props;
  const { market } = useAppContext();
  const { product } = useProductContext();
  const { allOffers } = useProductContext();
  const { getActiveFilters, getAdditiveFilters } = useOfferListFilterContext();
  const showAllVariantsExperiment = useShowAllVariantsExperiment();
  const activeAdditiveFilters = getAdditiveFilters().filter(f => f.active);
  const components: IFilterComponent[] = [];

  const CreateProductConditionComponent = () => {
    // Values are based on all values that can be found in offerlist + any offers in active addative filters
    const offersFilteredForConditions = filterOffers(
      activeAdditiveFilters,
      product.prices as PriceList
    );
    const productConditionValues = getProductConditionValues(
      offersFilteredForConditions
    );

    const showTitle = () => isMobile && components.length > 1;

    if (productConditionValues.length > 1) {
      components.push({
        key: 'conditions',
        buttonLabelComponent: null,
        selectorComponent: (
          <ProductConditions
            values={productConditionValues}
            showTitle={showTitle}
            isMobile={isMobile}
          />
        ),
      });
    }
  };
  CreateProductConditionComponent();

  const getValuesForVariantFilterType = (
    variantFilter: FilterType
  ): IVariantValue[] => {
    // Values should be based on all offers found in current filtered offerlist
    const activeFilters = getActiveFilters();

    // But we want to keep offers that are filtered out based on the selection of the specified variant
    const filtersExclSpecifiedType = activeFilters.filter(
      f => f.type !== variantFilter
    );

    const filteredOffers = filterOffers(
      filtersExclSpecifiedType,
      product.prices as PriceList
    );

    return getVariantValues(
      variantFilter as VariantType,
      filteredOffers,
      allOffers
    );
  };

  const CreateSizeVariantsComponent = () => {
    const show =
      showAllVariantsExperiment || isWhitelistedForSizes(product.category);
    if (!show) {
      return;
    }
    const variantValues = getValuesForVariantFilterType(
      FilterType.VARIANT_SIZE
    );
    if (allOffers.length > 1 && variantValues.length > 1) {
      components.push({
        key: 'size-variants',
        buttonLabelComponent: <SizeVariantsButtonLabel />,
        selectorComponent: (
          <SizeVariants distinctValues={variantValues} isMobile={isMobile} />
        ),
      });
    }
  };
  CreateSizeVariantsComponent();

  const CreateColorVariantsComponent = () => {
    const show =
      showAllVariantsExperiment ||
      (isWhitelistedForColors(market, product.category) &&
        !isBlacklistedForColors(product.category));
    if (!show) {
      return;
    }
    const variantValues = getValuesForVariantFilterType(
      FilterType.VARIANT_COLOR
    );
    if (allOffers.length > 1 && variantValues.length > 1) {
      components.push({
        key: 'color-variants',
        buttonLabelComponent: <ColorVariantsButtonLabel />,
        selectorComponent: (
          <ColorVariants distinctValues={variantValues} isMobile={isMobile} />
        ),
      });
    }
  };
  CreateColorVariantsComponent();

  const hasComponents = components.length > 0;
  const onlyConditionComponentVisible =
    components.length === 1 && components.find(c => c.key === 'conditions');
  const showModal = isMobile && hasComponents && !onlyConditionComponentVisible;

  return showModal ? (
    <ModalView components={components} />
  ) : (
    <>
      {components.map(component => (
        <Filter key={component.key}>{component.selectorComponent}</Filter>
      ))}
    </>
  );
};
